import { useContext, useState } from 'react';
import { Box, Button, Chip, Divider, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { AddOutlined, ArrowDownwardOutlined, ArrowUpwardOutlined, CloseOutlined, DeleteOutline, EditOutlined } from '@mui/icons-material';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import { GroupItemVisibilityDialog } from '../../../dialogs/surveys/group-item-visibility-dialog';
import {
  addPageBreakUpdates,
  groupItemsByPage,
  moveItemUpdates,
  pageNumbersOf,
  precedingItemsFor,
  removePageBreakUpdates,
  PageUpdate,
} from '../../../../shared/utils/survey-rules/group-pages';
import { IAddGroupItem, ISurveyQuestion, ISurveyQuestionGroupItem } from '../../../../shared/services/survey/survey.model';

export interface QuestionsPanelApi {
  updateGroupItemPages: (groupId: string, updates: PageUpdate[]) => Promise<unknown>;
  removeGroupItem: (groupId: string, itemId: string) => Promise<unknown>;
  updateGroupItem: (groupId: string, itemId: string, payload: unknown) => Promise<unknown>;
}

interface Props {
  groupId: string;
  /** Items already sorted by (page, sortOrder); use sortItemsByPage from group-pages. */
  sortedItems: ISurveyQuestionGroupItem[];
  locked?: boolean;
  /**
   * Reload the host's data. Return the reload promise: reorder keeps its controls
   * disabled until the refetch lands, so a second move cannot compute from stale rows.
   */
  onChanged: () => void | Promise<unknown>;
  /** Host opens its pick-an-existing-question flow targeting the given page. */
  onAddQuestion: (pageNumber: number) => void;
  /** When provided, Add Question also offers creating a brand-new question on the given page. */
  onCreateQuestion?: (pageNumber: number) => void;
  /** When provided, each row gets an edit affordance opening the host's question dialog. */
  onEditQuestion?: (item: ISurveyQuestionGroupItem) => void;
  /** Fallback source for question type labels when items carry none. */
  allQuestions?: ISurveyQuestion[];
  api?: QuestionsPanelApi;
}

/** The inline add affordance at the end of each page's question list. */
const AddQuestionRow = ({ locked, onClick }: { locked: boolean; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }) => (
  // variant='text' matters: the app theme defaults buttons to contained green,
  // which turns this quiet row into a solid bar.
  <Button
    variant='text'
    fullWidth
    disabled={locked}
    onClick={onClick}
    startIcon={<AddOutlined fontSize='small' />}
    sx={{
      mt: 0.75,
      py: 0.75,
      px: 1.5,
      justifyContent: 'flex-start',
      color: 'text.secondary',
      fontWeight: 400,
      bgcolor: 'transparent',
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 1,
      '&:hover': { color: 'primary.main', borderColor: 'primary.main', bgcolor: 'action.hover' },
    }}
  >
    Add question
  </Button>
);

/**
 * The question list of a question set: rows grouped into respondent-facing pages,
 * with page breaks, display conditions and removal. Hosted by the survey hub's
 * Questions tab and the standalone question set editor.
 */
export const SurveyQuestionsPanel = ({ groupId, sortedItems, locked = false, onChanged, onAddQuestion, onCreateQuestion, onEditQuestion, allQuestions = [], api }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const updatePages = api?.updateGroupItemPages
    ?? ((gid: string, updates: PageUpdate[]) => surveyAdminService.updateGroupItemPages(gid, updates));
  const removeItem = api?.removeGroupItem
    ?? ((gid: string, itemId: string) => surveyAdminService.removeGroupItem(gid, itemId));
  const updateItem = api?.updateGroupItem
    ?? ((gid: string, itemId: string, payload: unknown) => surveyAdminService.updateGroupItem(gid, itemId, payload as Partial<IAddGroupItem>));

  const [visibilityEditItem, setVisibilityEditItem] = useState<ISurveyQuestionGroupItem | null>(null);
  const [addMenu, setAddMenu] = useState<{ anchor: HTMLElement; pageNumber: number } | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  const pageNumbers = pageNumbersOf(sortedItems);
  const byPage = groupItemsByPage(sortedItems);

  const handleAddPageBreak = async (afterItem: ISurveyQuestionGroupItem) => {
    const updates = addPageBreakUpdates(sortedItems, afterItem);
    if (updates.length === 0) return;
    try {
      await updatePages(groupId, updates);
      onChanged();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to add page break', backgroundColor: 'text.primary' });
    }
  };

  const handleRemovePageBreak = async (pageNum: number) => {
    const updates = removePageBreakUpdates(sortedItems, pageNum);
    if (updates.length === 0) return;
    try {
      await updatePages(groupId, updates);
      onChanged();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to remove page break', backgroundColor: 'text.primary' });
    }
  };

  const handleMove = async (item: ISurveyQuestionGroupItem, direction: 'up' | 'down') => {
    const { pageUpdates, sortUpdates } = moveItemUpdates(sortedItems, item.id, direction);
    if (pageUpdates.length === 0 && sortUpdates.length === 0) return;
    setIsMoving(true);
    try {
      if (pageUpdates.length > 0) await updatePages(groupId, pageUpdates);
      for (const update of sortUpdates) {
        await updateItem(groupId, update.id, { sortOrder: update.sortOrder });
      }
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to move the question', backgroundColor: 'text.primary' });
    } finally {
      // Success or not, re-sync with what was actually persisted, and only then
      // re-enable the row controls: acting on stale rows could persist a
      // sortOrder tie or PATCH an item the reload is about to drop.
      try {
        await onChanged();
      } catch {
        // Hosts surface their own load errors.
      }
      setIsMoving(false);
    }
  };

  const handleRemoveItem = (item: ISurveyQuestionGroupItem) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Remove question',
      message: `Remove "${item.surveyQuestion?.prompt}" from this question set?`,
      buttonCopy: 'Remove',
      cancelButtonCopy: 'Cancel',
    }).then(async confirmed => {
      if (!confirmed) return;
      try {
        await removeItem(groupId, item.id);
        userNotificationsContext.openSnackbar({ open: true, message: 'Question removed', backgroundColor: 'primary.dark' });
        onChanged();
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to remove question';
        userNotificationsContext.openSnackbar({ open: true, message: msg, backgroundColor: 'text.primary' });
      }
    });
  };

  return (
    <Box>
      {/* The one add affordance: an inline row at the end of each page, opening a menu
          when a brand-new question can be written here. No competing buttons. */}
      <Menu anchorEl={addMenu?.anchor ?? null} open={Boolean(addMenu)} onClose={() => setAddMenu(null)}>
        <MenuItem onClick={() => { const p = addMenu!.pageNumber; setAddMenu(null); onCreateQuestion?.(p); }}>
          <ListItemText primary='New question' secondary='Write a question and add it to this survey' />
        </MenuItem>
        <MenuItem onClick={() => { const p = addMenu!.pageNumber; setAddMenu(null); onAddQuestion(p); }}>
          <ListItemText primary='Choose from library' secondary='Reuse a question your organisation already has' />
        </MenuItem>
      </Menu>

      {sortedItems.length === 0 && (
        <Box>
          <Typography color='text.secondary' variant='body2' sx={{ mb: 1.5 }}>
            No questions yet.
          </Typography>
          <AddQuestionRow
            locked={locked}
            onClick={e => (onCreateQuestion
              ? setAddMenu({ anchor: e.currentTarget, pageNumber: 1 })
              : onAddQuestion(1))}
          />
        </Box>
      )}

      {sortedItems.length > 0 && pageNumbers.map((pageNum, pageIdx) => {
        const pageItems = byPage.get(pageNum) ?? [];
        return (
          <Box key={pageNum}>
            {pageIdx > 0 && (
              <Stack direction='row' alignItems='center' spacing={1} sx={{ my: 1.5 }}>
                <Divider sx={{ flex: 1, borderColor: 'primary.main', opacity: 0.4 }} />
                <Chip
                  label={`Page ${pageNum}`}
                  size='small'
                  color='primary'
                  variant='outlined'
                  onDelete={locked ? undefined : () => handleRemovePageBreak(pageNum)}
                  deleteIcon={<CloseOutlined fontSize='small' />}
                />
                <Divider sx={{ flex: 1, borderColor: 'primary.main', opacity: 0.4 }} />
              </Stack>
            )}

            {pageNumbers.length > 1 && (
              <Typography variant='caption' color='text.secondary' fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                PAGE {pageNum} OF {pageNumbers.length}
              </Typography>
            )}

            <Stack spacing={0}>
              {pageItems.map((item, itemIdx) => {
                const typeLabel =
                  item.surveyQuestion?.surveyQuestionType?.label
                  ?? allQuestions.find(q => q.id === item.surveyQuestionId)?.surveyQuestionType?.label
                  ?? '';
                const isRequired = item.requiredOverride ?? item.surveyQuestion?.isRequired;
                const flatIdx = sortedItems.findIndex(i => i.id === item.id);
                const isFirstOverall = flatIdx === 0;
                const isLastOverall = flatIdx === sortedItems.length - 1;
                return (
                  <Box key={item.id}>
                    <Box sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                      <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap'>
                        <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }}>
                          {item.surveyQuestion?.prompt ?? '—'}
                        </Typography>
                        <Stack direction='row' spacing={0.5} alignItems='center' flexShrink={0}>
                          {typeLabel && <Chip label={typeLabel} size='small' variant='outlined' />}
                          {isRequired && <Chip label='Required' size='small' color='primary' />}
                          <Tooltip title={item.visibilityRule ? 'Edit display conditions' : 'Add display conditions'}>
                            <Chip
                              label={item.visibilityRule
                                ? `${item.visibilityRule.allOf?.length ?? 0} condition(s)`
                                : 'No conditions'}
                              size='small'
                              color={item.visibilityRule ? 'info' : 'default'}
                              variant='outlined'
                              onClick={locked || isMoving ? undefined : () => setVisibilityEditItem(item)}
                              clickable={!locked && !isMoving}
                              disabled={locked || isMoving}
                            />
                          </Tooltip>
                          <Tooltip title='Move up'>
                            <span>
                              <IconButton size='small' disabled={locked || isMoving || isFirstOverall} onClick={() => handleMove(item, 'up')}>
                                <ArrowUpwardOutlined fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Move down'>
                            <span>
                              <IconButton size='small' disabled={locked || isMoving || isLastOverall} onClick={() => handleMove(item, 'down')}>
                                <ArrowDownwardOutlined fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {onEditQuestion && (
                            <Tooltip title='Edit question'>
                              <span>
                                <IconButton size='small' disabled={locked || isMoving} onClick={() => onEditQuestion(item)}>
                                  <EditOutlined fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title='Remove from question set'>
                            <span>
                              <IconButton size='small' disabled={locked || isMoving} onClick={() => handleRemoveItem(item)}>
                                <DeleteOutline fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Box>
                    {itemIdx < pageItems.length - 1 && (
                      // A slim always-visible affordance between rows: quiet enough not to
                      // compete with the rows, present enough to be found without hovering.
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 18,
                        }}
                      >
                        <Button
                          size='small'
                          variant='text'
                          disabled={locked || isMoving}
                          onClick={() => handleAddPageBreak(item)}
                          sx={{
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            bgcolor: 'transparent',
                            minWidth: 0,
                            px: 1,
                            py: 0,
                            opacity: 0.65,
                            transition: 'opacity 0.15s ease',
                            '&:hover': { color: 'primary.main', bgcolor: 'transparent', opacity: 1 },
                          }}
                        >
                          + page break
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
            <AddQuestionRow
              locked={locked}
              onClick={e => (onCreateQuestion
                ? setAddMenu({ anchor: e.currentTarget, pageNumber: pageNum })
                : onAddQuestion(pageNum))}
            />
          </Box>
        );
      })}

      {visibilityEditItem && (
        <GroupItemVisibilityDialog
          open={Boolean(visibilityEditItem)}
          onClose={() => setVisibilityEditItem(null)}
          successHandler={() => { setVisibilityEditItem(null); onChanged(); }}
          groupId={groupId}
          item={visibilityEditItem}
          precedingItems={precedingItemsFor(sortedItems, visibilityEditItem)}
          api={api ? { updateGroupItem: api.updateGroupItem } : undefined}
        />
      )}
    </Box>
  );
};
